import Head from 'next/head';
import Link from 'next/link';
import { useEffect, useState, useCallback, type ReactElement } from 'react';
import { useRouter } from 'next/router';
import { Briefcase, ArrowLeft, Building2, Shield } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { WorkOSSSOButton } from '@/components/auth/workos-sso-button';
import { WorkOSOrganizationSelector } from '@/components/auth/workos-organization-selector';

export default function CorporateLogin(): ReactElement {
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const router = useRouter();

  const checkProfileAndRedirect = useCallback(async (token: string): Promise<void> => {
    try {
      const response = await fetch('/api/user-profile-status', {
        method: 'GET',
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to profile page if user hasn't completed their profile
        if (data.isFirstTime || !data.hasCompletedProfile) {
          router.push('/profile');
        } else {
          router.push('/dashboard');
        }
      } else {
        // If we can't check profile status, redirect to dashboard
        router.push('/dashboard');
      }
    } catch (error) {
      console.error('Error checking profile status:', error);
      // If there's an error, redirect to dashboard
      router.push('/dashboard');
    }
  }, [router]);

  // Check for query parameters for messages and errors
  useEffect((): void => {
    const { error: queryError, message: queryMessage } = router.query;
    if (queryError) {
      setError(String(queryError).replace(/_/g, ' '));
    }
    if (queryMessage) {
      setMessage(String(queryMessage).replace(/_/g, ' '));
    }
  }, [router.query]);

  useEffect((): (() => void) => {
    const run = async (): Promise<void> => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        await checkProfileAndRedirect(data.session.access_token);
      }
    };
    void run();
    const { data: listener } = supabase.auth.onAuthStateChange(async (_e, session) => {
      if (session) {
        await checkProfileAndRedirect(session.access_token);
      }
    });
    return () => listener.subscription.unsubscribe();
  }, [router, checkProfileAndRedirect]);

  const handleSearch = (): void => {
    if (email) {
      setIsSearching(true);
      setSelectedOrganization(null);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
    // Reset search state when email changes
    if (isSearching) {
      setIsSearching(false);
      setSelectedOrganization(null);
    }
  };

  return (
    <>
      <Head>
        <title>Corporate Login – KEM Discovery</title>
        <meta name="description" content="Access your corporate KEM Discovery account with enhanced team insights and organizational analytics." />
      </Head>
      
      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center py-12">
        <div className="w-full max-w-md space-y-6">
          {/* Back Link */}
          <div className="flex items-center text-sm text-muted-foreground">
            <Link href="/" className="flex items-center gap-2 hover:text-foreground transition-colors">
              <ArrowLeft className="size-4" />
              Back to home
            </Link>
          </div>

          {/* Main Card */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-blue-50/30 dark:from-gray-900 dark:to-blue-950/30 w-[400px]">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto size-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white grid place-items-center shadow-lg mb-4">
                <Building2 className="size-8" />
              </div>
              <CardTitle className="text-2xl font-bold">
                Corporate Portal
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Access your organizational dashboard with enhanced team insights
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                  {error}
                </div>
              )}

              {message && (
                <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800/20 text-green-800 dark:text-green-200 text-sm">
                  {message}
                </div>
              )}

              {/* SSO Section */}
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-lg font-semibold mb-2">Sign in with SSO</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Enter your corporate email to find your organization
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="corp-email" className="text-sm font-medium">
                      Corporate Email
                    </label>
                    <Input 
                      id="corp-email" 
                      type="email" 
                      value={email} 
                      onChange={handleEmailChange} 
                      placeholder="you@company.com"
                      className="h-11"
                      required 
                    />
                  </div>

                  <Button 
                    onClick={handleSearch}
                    disabled={!email}
                    className="w-full h-11 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium"
                  >
                    Search for Organization
                  </Button>

                  {/* Organization Selection Area - Fixed Height */}
                  <div className="min-h-[200px] w-full">
                    {isSearching && !selectedOrganization ? (
                      <WorkOSOrganizationSelector
                        email={email}
                        onOrganizationSelect={setSelectedOrganization}
                      />
                    ) : selectedOrganization && selectedOrganization !== 'new' ? (
                      <div className="space-y-3">
                        <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <p className="text-sm text-blue-800 dark:text-blue-200">
                            Ready to sign in with your organization
                          </p>
                        </div>
                        <WorkOSSSOButton
                          organizationId={selectedOrganization}
                          variant="default"
                          size="default"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedOrganization(null)}
                          className="w-full"
                        >
                          Choose different organization
                        </Button>
                      </div>
                    ) : selectedOrganization === 'new' ? (
                      <div className="p-4 border rounded-lg text-center">
                        <p className="text-sm text-muted-foreground mb-3">
                          Contact your administrator to set up SSO for your organization.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedOrganization(null)}
                          className="w-full"
                        >
                          Back to organization selection
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Want to set up corporate access?{' '}
                  <Link href="/corporate" className="text-blue-600 hover:text-blue-500 font-medium underline underline-offset-4">
                    Get Started
                  </Link>
                </p>
                <p className="text-sm text-muted-foreground">
                  Looking for personal access?{' '}
                  <Link href="/individual/login" className="text-purple-600 hover:text-purple-500 font-medium underline underline-offset-4">
                    Individual Login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Trust Indicators */}
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Shield className="size-3" />
                <span>Enterprise Security</span>
              </div>
              <div className="flex items-center gap-1">
                <Briefcase className="size-3" />
                <span>SSO Only</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}



